import React, { useState, useMemo } from 'react';
import { DollarSign, Clock, TrendingUp, Users, RefreshCw, FileText, Printer, ChevronDown, ChevronUp, X, Target, AlertCircle, TrendingDown, PieChart, Plus, Pencil, Trash2, Save } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { exportarPDF, exportarWord } from '../utils/exportDoc';
import { statusFollowup, ESTAGIOS } from './Prospeccao';
import { useEmpresa } from '../contexts/EmpresaContext';
import { toast } from '../utils/toast';
import { cnpjFormatado } from '../utils/cnpj';

// Referência estável de array vazio — alinhado com FinanceiroAvancado
const EMPTY_FLUXO_CAIXA = Object.freeze([]);
const EMPTY_OBJ = Object.freeze({});
const CAT_PADRAO = ['Banco','Impostos','Fornecedores','Salários','Empréstimos','Outros'];

// Resolve valor namespaced por empresa (mesmo padrão do FinanceiroAvancado).
const resolverNamespaceEmpresa = (mapaPorEmpresa, slug, legado, vazio) => {
  if (mapaPorEmpresa && mapaPorEmpresa[slug] !== undefined) return mapaPorEmpresa[slug];
  if (slug === 'kzero-consultoria' && legado !== undefined && legado !== null) return legado;
  return vazio;
};

// Formata data dd/mm/aaaa
const fmtData = (str) => {
  if (!str) return '—';
  const [y, m, d] = str.split('-');
  return `${d}/${m}/${y}`;
};

const fmtMoeda = (v) => parseFloat(parseFloat(v || 0).toFixed(2)).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const nl2br = (text) => (text || '').replace(/\n/g, '<br>');

// Gera HTML do relatório de prestação de contas para um cliente
function htmlPrestacaoContas({ cliente, agendamentos, periodo, anotacoes = [] }) {
  const valorHora = parseFloat(cliente.valorHora || 0);
  const sorted = [...agendamentos].sort((a, b) => a.data?.localeCompare(b.data));

  // Recalcula horas e valor por linha usando 1 decimal (consistente)
  const totalHoras = sorted.reduce((s, a) => s + parseFloat(parseFloat(a.horas || 0).toFixed(1)), 0);
  const total = parseFloat(totalHoras.toFixed(1)) * valorHora;

  const linhas = sorted.map(a => {
    const horas = parseFloat(parseFloat(a.horas || 0).toFixed(1));
    const valorLinha = horas * valorHora;
    // 1ª prioridade: anotação vinculada diretamente ao agendamento (por id).
    // IMPORTANTE: exigir que o clienteId da anotação também bata com o do evento.
    // Sem isso, vínculo stale (ex: evento que mudou de cliente após auto-criar a anotação)
    // arrastava descrição de outro cliente pro relatório.
    const porId = anotacoes.find(n =>
      n.agendamentoId
      && String(n.agendamentoId) === String(a.id)
      && String(n.clienteId) === String(a.cliente)
    );
    // 2ª prioridade: anotação do mesmo cliente+data com conteúdo preenchido (mais recente primeiro)
    const porData = !porId
      ? [...anotacoes]
          .filter(n => String(n.clienteId) === String(a.cliente) && n.data === a.data && n.conteudo?.trim())
          .sort((x, y) => String(y.id).localeCompare(String(x.id)))[0]
      : null;
    const anotacao = porId || porData;
    const descricao = anotacao?.conteudo?.trim() || a.observacoes || '—';
    return `
    <tr>
      <td>${fmtData(a.data)}</td>
      <td>${a.horaInicio || '—'} – ${a.horaFinal || '—'}</td>
      <td>${horas.toFixed(1)}h</td>
      <td>${nl2br(descricao)}</td>
      <td style="text-align:right; color:#059669; font-weight:600">R$ ${fmtMoeda(valorLinha)}</td>
    </tr>
  `;
  }).join('');

  return `
    <h1>Prestação de Contas — ${cliente.nome || cliente}</h1>
    <p class="campo"><span class="label">Período:</span> <span class="valor">${periodo}</span></p>
    ${cnpjFormatado(cliente.cnpj) ? `<p class="campo"><span class="label">CNPJ:</span> <span class="valor">${cnpjFormatado(cliente.cnpj)}</span></p>` : ''}
    ${cliente.email ? `<p class="campo"><span class="label">Contato:</span> <span class="valor">${cliente.email}</span></p>` : ''}
    <hr class="separador"/>
    <h2>Detalhamento dos Atendimentos</h2>
    <table>
      <thead>
        <tr>
          <th>Data</th>
          <th>Horário</th>
          <th>Horas</th>
          <th>Descrição / Observações</th>
          <th style="text-align:right">Valor</th>
        </tr>
      </thead>
      <tbody>
        ${linhas}
      </tbody>
    </table>
    <div style="margin-top:20px; padding:16px; background:#f0fdf4; border:1px solid #86efac; border-radius:8px;">
      <div style="display:flex; justify-content:space-between; margin-bottom:6px;">
        <strong>Total de Horas:</strong>
        <strong>${totalHoras.toFixed(1)}h</strong>
      </div>
      <div style="display:flex; justify-content:space-between; font-size:18px; color:#059669;">
        <strong>Valor Total:</strong>
        <strong>R$ ${fmtMoeda(total)}</strong>
      </div>
    </div>
    <div class="rodape">
      <div class="assinatura">
        <div class="linha-assinatura">Assinatura do Prestador</div>
      </div>
      <div class="assinatura">
        <div class="linha-assinatura">Assinatura do Cliente</div>
      </div>
    </div>
  `;
}

// Card-resumo de indicador mensal com variação vs mês anterior.
// `corPositiva` define se subir é bom (faturamento, distribuição) ou ruim (despesas).
// `onClick` opcional torna o card clicável (abre modal de detalhe do mês).
function IndicadorCard({ label, valor, variacao, cor, icone, corPositiva = false, onClick = null }) {
  const temVar = variacao !== null && Number.isFinite(variacao);
  const subindo = temVar && variacao > 0;
  const corVar = !temVar
    ? '#5f5874'
    : subindo
      ? (corPositiva ? '#10b981' : '#ef4444')
      : (corPositiva ? '#ef4444' : '#10b981');
  return (
    <div
      onClick={onClick || undefined}
      title={onClick ? 'Clique para ver/editar lançamentos do mês' : undefined}
      style={{
        background: '#f7f5fb',
        border: '1px solid #e5e0ef',
        borderRadius: 10,
        padding: '0.9rem 1rem',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'border-color 0.15s, transform 0.15s',
      }}
      onMouseEnter={onClick ? (e) => { e.currentTarget.style.borderColor = cor; } : undefined}
      onMouseLeave={onClick ? (e) => { e.currentTarget.style.borderColor = '#e5e0ef'; } : undefined}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 12, color: '#5f5874' }}>{label}</span>
        {icone}
      </div>
      <span style={{ fontSize: '1.4rem', fontWeight: 700, color: cor }}>R$ {fmtMoeda(valor)}</span>
      <span style={{ fontSize: 11, color: corVar }}>
        {temVar
          ? `${subindo ? '▲' : '▼'} ${Math.abs(variacao).toFixed(1)}% vs mês anterior`
          : 'sem comparação'}
      </span>
    </div>
  );
}

export function Painel({ data = {}, setConfigEstagiaria, setFluxoCaixa: setFluxoCaixaLegado, setFluxoCaixaPorEmpresa, isAdmin = false }) {
  data = { agendamentos: [], clientes: [], fluxoCaixa: [], fluxoCaixaPorEmpresa: {}, fin_acertos: {}, fin_categorias: null, configEstagiaria: { valorHora: 0 }, ...data };
  const hoje = new Date();
  const primeiroDiaMes = `${hoje.getFullYear()}-${String(hoje.getMonth()+1).padStart(2,'0')}-01`;
  const hojeStr = hoje.toISOString().split('T')[0];

  const [dataInicial, setDataInicial] = useState(primeiroDiaMes);
  const [dataFinal, setDataFinal]     = useState(hojeStr);
  const [clientesSelecionados, setClientesSelecionados] = useState([]);
  const [modalRelatorio, setModalRelatorio] = useState(false);
  const [clienteExpandido, setClienteExpandido] = useState(null);
  const [dropdownAberto, setDropdownAberto] = useState(false);
  const [rateEditando, setRateEditando] = useState(false);
  const [rateInput, setRateInput] = useState('');
  const [mesDetalhe, setMesDetalhe] = useState(null); // ym do mês aberto no modal

  // ── fluxoCaixa namespaced por empresa (alinhado com FinanceiroAvancado) ──
  const { empresaAtiva } = useEmpresa();
  const slugEmp = empresaAtiva?.slug || 'kzero-consultoria';
  const fluxoCaixaLegado = data.fluxoCaixa;
  const mapaFluxo = data.fluxoCaixaPorEmpresa;
  const fluxoCaixa = useMemo(() => {
    if (mapaFluxo && mapaFluxo[slugEmp] !== undefined) return mapaFluxo[slugEmp];
    if (slugEmp === 'kzero-consultoria' && fluxoCaixaLegado) return fluxoCaixaLegado;
    return EMPTY_FLUXO_CAIXA;
  }, [mapaFluxo, slugEmp, fluxoCaixaLegado]);

  const setFluxoCaixa = (valorOuFn) => {
    if (!setFluxoCaixaPorEmpresa) {
      setFluxoCaixaLegado?.(valorOuFn);
      return;
    }
    setFluxoCaixaPorEmpresa(prevMapa => {
      const mapa = prevMapa || {};
      const fluxoAtual = mapa[slugEmp] !== undefined
        ? mapa[slugEmp]
        : (slugEmp === 'kzero-consultoria' ? (fluxoCaixaLegado || []) : []);
      const novoFluxo = typeof valorOuFn === 'function' ? valorOuFn(fluxoAtual) : valorOuFn;
      return { ...mapa, [slugEmp]: novoFluxo };
    });
  };

  const categoriasDisponiveis = useMemo(() => {
    const resolved = resolverNamespaceEmpresa(data.fin_categoriasPorEmpresa, slugEmp, data.fin_categorias, CAT_PADRAO);
    return Array.isArray(resolved) && resolved.length > 0 ? resolved : CAT_PADRAO;
  }, [data.fin_categoriasPorEmpresa, slugEmp, data.fin_categorias]);

  const podeEditar = !!(setFluxoCaixaPorEmpresa || setFluxoCaixaLegado);

  const classifDe = (a) => a.classificacao || a.tipo || 'CONSULTORIA';
  const isEstagiaria = (a) => classifDe(a) === 'ESTAGIARIA';
  const isReuniaoNaoFaturavel = (a) => classifDe(a) === 'REUNIAO' && !a.faturarReuniao;

  const agsFiltrados = useMemo(() =>
    data.agendamentos.filter(a => a.data && a.data >= dataInicial && a.data <= dataFinal),
    [data.agendamentos, dataInicial, dataFinal]
  );

  // Entries de faturamento: tudo EXCETO Estagiária e reuniões não-faturáveis
  const agsFaturaveis = useMemo(
    () => agsFiltrados.filter(a => !isEstagiaria(a) && !isReuniaoNaoFaturavel(a)),
    [agsFiltrados]
  );
  // Entries de Estagiária: usadas apenas no cálculo "a pagar"
  const agsEstagiaria = useMemo(() => agsFiltrados.filter(isEstagiaria), [agsFiltrados]);

  // Recalcula valor de cada agendamento faturável usando horas × valorHora do cliente
  const calcValorAg = (a) => {
    const cliente = data.clientes.find(c => String(c.id) === String(a.cliente));
    const valorHora = parseFloat(cliente?.valorHora || 0);
    const horas = parseFloat(parseFloat(a.horas || 0).toFixed(1));
    return horas * valorHora;
  };

  const totalHoras     = agsFaturaveis.reduce((s, a) => s + parseFloat(parseFloat(a.horas || 0).toFixed(1)), 0);
  const totalFaturado  = agsFaturaveis.reduce((s, a) => s + calcValorAg(a), 0);
  const valorMedioHora = totalHoras > 0 ? totalFaturado / totalHoras : 0;

  // Cálculo "a pagar — Estagiária"
  const rateEstagiaria = parseFloat(data.configEstagiaria?.valorHora || 0);
  const horasEstagiaria = agsEstagiaria.reduce((s, a) => s + parseFloat(parseFloat(a.horas || 0).toFixed(1)), 0);
  const totalAPagarEstagiaria = horasEstagiaria * rateEstagiaria;

  const salvarRate = () => {
    const novo = parseFloat(rateInput.replace(',', '.')) || 0;
    if (setConfigEstagiaria) {
      setConfigEstagiaria(prev => ({ ...(prev || {}), valorHora: novo }));
    }
    setRateEditando(false);
  };

  const faturamentoPorCliente = useMemo(() => {
    const mapa = {};
    agsFaturaveis.forEach(a => {
      const cliente = data.clientes.find(c => String(c.id) === String(a.cliente));
      const nome = cliente?.nome || 'Sem cliente';
      mapa[nome] = (mapa[nome] || 0) + calcValorAg(a);
    });
    return Object.entries(mapa).sort((a, b) => b[1] - a[1]);
  }, [agsFaturaveis, data.clientes]);

  const horasPorCliente = useMemo(() => {
    const mapa = {};
    agsFaturaveis.forEach(a => {
      const cliente = data.clientes.find(c => String(c.id) === String(a.cliente));
      const nome = cliente?.nome || 'Sem cliente';
      mapa[nome] = (mapa[nome] || 0) + parseFloat(parseFloat(a.horas || 0).toFixed(1));
    });
    return Object.entries(mapa).sort((a, b) => b[1] - a[1]);
  }, [agsFaturaveis, data.clientes]);

  // Dados agrupados por cliente para o relatório
  const dadosPorCliente = useMemo(() => {
    const selecionados = clientesSelecionados.length > 0
      ? clientesSelecionados
      : data.clientes.map(c => String(c.id));

    return selecionados.map(cid => {
      const cliente = data.clientes.find(c => String(c.id) === cid);
      if (!cliente) return null;
      const ags = agsFaturaveis.filter(a => String(a.cliente) === cid).sort((a, b) => a.data?.localeCompare(b.data));
      return {
        cliente,
        agendamentos: ags,
        total: ags.reduce((s, a) => s + calcValorAg(a), 0),
        totalHoras: ags.reduce((s, a) => s + parseFloat(parseFloat(a.horas || 0).toFixed(1)), 0),
      };
    }).filter(Boolean).filter(d => d.agendamentos.length > 0);
  }, [clientesSelecionados, agsFaturaveis, data.clientes]);

  const toggleCliente = (id) => {
    const sid = String(id);
    setClientesSelecionados(prev =>
      prev.includes(sid) ? prev.filter(x => x !== sid) : [...prev, sid]
    );
  };

  const periodo = `${fmtData(dataInicial)} a ${fmtData(dataFinal)}`;

  const emitirPDF = (clienteData) => {
    const html = htmlPrestacaoContas({ ...clienteData, periodo, anotacoes: anotacoesNamespaced });
    exportarPDF(`Prestação de Contas — ${clienteData.cliente.nome}`, html);
  };

  const emitirWord = (clienteData) => {
    const html = htmlPrestacaoContas({ ...clienteData, periodo, anotacoes: anotacoesNamespaced });
    exportarWord(`PrestacaoContas_${clienteData.cliente.nome.replace(/\s+/g, '_')}`, html);
  };

  const emitirTodosPDF = () => {
    dadosPorCliente.forEach(d => emitirPDF(d));
  };

  const clientesComAgs = useMemo(() =>
    data.clientes.filter(c => agsFaturaveis.some(a => String(a.cliente) === String(c.id))),
    [data.clientes, agsFaturaveis]
  );

  // ── Indicadores mensais (últimos 12 meses) ──────────────────────────────
  // Faturamento bruto: lançamentos RECEITA do fluxoCaixa por mesCompetencia (fallback: data)
  // Despesas: lançamentos não-RECEITA do fluxoCaixa, exclui antecipações e rejeitados, por data
  // Distribuição de Lucros: soma de valorPago das quotas em fin_acertos por mês
  const acertosNamespaced = useMemo(
    () => resolverNamespaceEmpresa(data.fin_acertosPorEmpresa, slugEmp, data.fin_acertos, EMPTY_OBJ),
    [data.fin_acertosPorEmpresa, slugEmp, data.fin_acertos]
  );
  // Anotações namespaced: usadas pelo relatório de prestação de contas (htmlPrestacaoContas).
  const anotacoesNamespaced = useMemo(
    () => resolverNamespaceEmpresa(data.anotacoesPorEmpresa, slugEmp, data.anotacoes, EMPTY_FLUXO_CAIXA),
    [data.anotacoesPorEmpresa, slugEmp, data.anotacoes]
  );
  const indicadoresMensais = useMemo(() => {
    const fluxo = fluxoCaixa || [];
    const acertos = acertosNamespaced || {};
    const ref = new Date();
    const meses = [];
    for (let i = 11; i >= 0; i--) {
      const dt = new Date(ref.getFullYear(), ref.getMonth() - i, 1);
      const ym = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`;
      const label = dt.toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' }).replace('.', '');
      meses.push({ ym, label });
    }

    return meses.map(({ ym, label }) => {
      const faturamento = fluxo
        .filter(f => {
          if ((f.tipo || '').toUpperCase() !== 'RECEITA') return false;
          if (f.status === 'rejeitado') return false;
          const m = f.mesCompetencia || (f.data || '').substring(0, 7);
          return m === ym;
        })
        .reduce((s, f) => s + parseFloat(f.valor || 0), 0);

      const despesas = fluxo
        .filter(f => {
          if ((f.tipo || '').toUpperCase() === 'RECEITA') return false;
          if (f.status === 'rejeitado') return false;
          if ((f.categoria || '').toLowerCase().includes('antecipa')) return false;
          return (f.data || '').startsWith(ym);
        })
        .reduce((s, f) => s + parseFloat(f.valor || 0), 0);

      const distribuicao = Object.values(acertos[ym] || {})
        .reduce((s, q) => {
          const vp = q?.valorPago;
          return s + (vp !== null && vp !== undefined ? parseFloat(vp) || 0 : 0);
        }, 0);

      return {
        mes: label,
        ym,
        Faturamento: Math.round(faturamento * 100) / 100,
        Despesas: Math.round(despesas * 100) / 100,
        'Distribuição': Math.round(distribuicao * 100) / 100,
      };
    });
  }, [fluxoCaixa, acertosNamespaced]);

  const resumoMesAtual = useMemo(() => {
    const ymAtualFallback = `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}`;
    const atual = indicadoresMensais[indicadoresMensais.length - 1] || { Faturamento: 0, Despesas: 0, 'Distribuição': 0, ym: ymAtualFallback };
    const anterior = indicadoresMensais[indicadoresMensais.length - 2] || { Faturamento: 0, Despesas: 0, 'Distribuição': 0 };
    const variacao = (a, b) => b === 0 ? null : ((a - b) / Math.abs(b)) * 100;
    return {
      atual,
      varFat: variacao(atual.Faturamento, anterior.Faturamento),
      varDesp: variacao(atual.Despesas, anterior.Despesas),
      varDist: variacao(atual['Distribuição'], anterior['Distribuição']),
    };
  }, [indicadoresMensais]);

  // Follow-ups pendentes (atrasados + hoje + próximos 3 dias)
  const followupsPendentes = useMemo(() => {
    const lista = (data.oportunidades || [])
      .filter(o => o.proximoContato && !['GANHO', 'PERDIDO'].includes(o.estagio))
      .map(o => ({ ...o, _status: statusFollowup(o.proximoContato) }))
      .filter(o => o._status && o._status.diasRestantes <= 3)
      .sort((a, b) => a._status.diasRestantes - b._status.diasRestantes);
    return lista;
  }, [data.oportunidades]);

  return (
    <div style={s.container}>
      <div style={s.tituloRow}>
        <h2 style={s.titulo}>Painel</h2>
        <button style={s.btnRelatorio} onClick={() => setModalRelatorio(true)}>
          <FileText size={16}/> Relatório de Cobrança
        </button>
      </div>

      {/* Filtro de período */}
      <div style={s.filtroCard}>
        <span style={s.filtroLabel}>Período de análise</span>
        <div style={s.filtroRow}>
          <div style={s.filtroGrupo}>
            <label style={s.label}>Data Inicial</label>
            <input type="date" style={s.input} value={dataInicial} onChange={e => setDataInicial(e.target.value)} />
          </div>
          <div style={s.filtroGrupo}>
            <label style={s.label}>Data Final</label>
            <input type="date" style={s.input} value={dataFinal} onChange={e => setDataFinal(e.target.value)} />
          </div>
          <button style={s.btnAtualizar} onClick={() => {}}>
            <RefreshCw size={15} /> Atualizar
          </button>
        </div>
      </div>

      {/* Cards */}
      <div className="mobile-grid-1" style={s.cardsGrid}>
        <div style={s.card}>
          <div style={s.cardTop}><span style={s.cardLabel}>Total Faturado</span><DollarSign size={20} color="#10b981"/></div>
          <span style={{ ...s.cardValor, color: '#10b981' }}>R$ {fmtMoeda(totalFaturado)}</span>
        </div>
        <div style={s.card}>
          <div style={s.cardTop}><span style={s.cardLabel}>Total de Horas</span><Clock size={20} color="#3b82f6"/></div>
          <span style={{ ...s.cardValor, color: '#3b82f6' }}>{totalHoras.toFixed(1)}h</span>
        </div>
        <div style={s.card}>
          <div style={s.cardTop}><span style={s.cardLabel}>Valor méd/Hora</span><TrendingUp size={20} color="#8b5cf6"/></div>
          <span style={{ ...s.cardValor, color: '#8b5cf6' }}>R$ {fmtMoeda(valorMedioHora)}</span>
        </div>
        <div style={s.card}>
          <div style={s.cardTop}><span style={s.cardLabel}>Total de Clientes</span><Users size={20} color="#f59e0b"/></div>
          <span style={{ ...s.cardValor, color: '#f59e0b' }}>{data.clientes.length}</span>
        </div>
      </div>

      {/* Follow-ups de prospecção pendentes */}
      {followupsPendentes.length > 0 && (
        <div style={{ background: '#ffffff', border: '1px solid #f59e0b', borderLeft: '4px solid #f59e0b', borderRadius: 12, padding: '1rem 1.25rem', marginBottom: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '0.75rem' }}>
            <AlertCircle size={18} color="#f59e0b" />
            <strong style={{ color: '#1c1530', fontSize: 14 }}>Follow-ups de prospecção pendentes</strong>
            <span style={{ background: '#f59e0b', color: 'white', borderRadius: 12, padding: '1px 8px', fontSize: 11, fontWeight: 700 }}>{followupsPendentes.length}</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
            {followupsPendentes.slice(0, 5).map(o => {
              const cor = o._status.tipo === 'atrasado' ? '#ef4444' : o._status.tipo === 'hoje' ? '#f59e0b' : '#3b82f6';
              const est = ESTAGIOS.find(e => e.id === o.estagio);
              return (
                <div key={o.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem', padding: '6px 10px', background: '#ffffff', borderRadius: 6 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <Target size={13} color={cor} />
                    <span style={{ color: '#1c1530', fontSize: 13, fontWeight: 600 }}>{o.cliente || o.empresa}</span>
                    {est && <span style={{ color: est.cor, fontSize: 11, border: `1px solid ${est.cor}`, borderRadius: 10, padding: '1px 8px' }}>{est.label}</span>}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    {(o.proximoResponsavel || o.responsavel) && <span style={{ fontSize: 11, color: '#5f5874' }}>👤 {o.proximoResponsavel || o.responsavel}</span>}
                    <span style={{ color: cor, fontSize: 11, border: `1px solid ${cor}`, borderRadius: 10, padding: '1px 8px', fontWeight: 600 }}>{o._status.label}</span>
                  </div>
                </div>
              );
            })}
            {followupsPendentes.length > 5 && (
              <div style={{ fontSize: 11, color: '#5f5874', textAlign: 'center', padding: '4px 0' }}>+ {followupsPendentes.length - 5} pendentes — abra o módulo Prospecção para ver todos</div>
            )}
          </div>
        </div>
      )}

      {/* Card "A pagar — Estagiária" */}
      <div style={{ ...s.card, borderLeft: '4px solid #ec4899', marginBottom: '1rem' }}>
        <div style={s.cardTop}>
          <span style={s.cardLabel}>A pagar — Estagiária</span>
          <Clock size={20} color="#ec4899" />
        </div>
        <span style={{ ...s.cardValor, color: '#ec4899' }}>R$ {fmtMoeda(totalAPagarEstagiaria)}</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, fontSize: 12, color: '#5f5874' }}>
          <span>{horasEstagiaria.toFixed(1)}h no período</span>
          <span>·</span>
          {rateEditando ? (
            <>
              <span>R$</span>
              <input
                type="number" step="0.01" autoFocus
                value={rateInput}
                onChange={e => setRateInput(e.target.value)}
                onBlur={salvarRate}
                onKeyDown={e => { if (e.key === 'Enter') salvarRate(); if (e.key === 'Escape') setRateEditando(false); }}
                style={{ width: 80, background: '#ffffff', border: '1px solid #e5e0ef', color: '#1c1530', borderRadius: 4, padding: '2px 6px' }}
              />
              <span>/hora</span>
            </>
          ) : (
            <>
              <span>R$ {fmtMoeda(rateEstagiaria)}/hora</span>
              {isAdmin && (
                <button
                  onClick={() => { setRateInput(String(rateEstagiaria || '')); setRateEditando(true); }}
                  style={{ background: 'none', border: '1px solid #e5e0ef', color: '#ec4899', borderRadius: 4, padding: '1px 8px', cursor: 'pointer', fontSize: 11 }}
                >editar</button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Indicadores mensais — últimos 12 meses */}
      <div style={s.secao}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem', flexWrap: 'wrap', gap: 8 }}>
          <h3 style={{ ...s.secaoTitulo, marginBottom: 0 }}>Indicadores Mensais</h3>
          <span style={{ fontSize: 11, color: '#5f5874' }}>últimos 12 meses</span>
        </div>

        {/* Cards-resumo do mês corrente */}
        <div className="mobile-grid-1" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.75rem', marginBottom: '1rem' }}>
          <IndicadorCard
            label="Faturamento Bruto"
            valor={resumoMesAtual.atual.Faturamento}
            variacao={resumoMesAtual.varFat}
            cor="#10b981"
            icone={<DollarSign size={18} color="#10b981" />}
            corPositiva
            onClick={podeEditar ? () => setMesDetalhe(resumoMesAtual.atual.ym) : null}
          />
          <IndicadorCard
            label="Despesas"
            valor={resumoMesAtual.atual.Despesas}
            variacao={resumoMesAtual.varDesp}
            cor="#ef4444"
            icone={<TrendingDown size={18} color="#ef4444" />}
            onClick={podeEditar ? () => setMesDetalhe(resumoMesAtual.atual.ym) : null}
          />
          <IndicadorCard
            label="Distribuição de Lucros"
            valor={resumoMesAtual.atual['Distribuição']}
            variacao={resumoMesAtual.varDist}
            cor="#8b5cf6"
            icone={<PieChart size={18} color="#8b5cf6" />}
            corPositiva
          />
        </div>

        {podeEditar && (
          <div style={{ fontSize: 11, color: '#5f5874', marginBottom: 6 }}>
            💡 Clique em um card ou em uma barra do gráfico para ver, editar ou lançar receitas/despesas daquele mês.
          </div>
        )}

        {/* Gráfico de barras agrupadas */}
        <div style={{ width: '100%', height: 320 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={indicadoresMensais}
              margin={{ top: 8, right: 16, left: 0, bottom: 4 }}
              onClick={(e) => {
                if (!podeEditar) return;
                const ym = e?.activePayload?.[0]?.payload?.ym;
                if (ym) setMesDetalhe(ym);
              }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e0ef" />
              <XAxis dataKey="mes" tick={{ fill: '#5f5874', fontSize: 11 }} stroke="#e5e0ef" />
              <YAxis
                tick={{ fill: '#5f5874', fontSize: 11 }}
                stroke="#e5e0ef"
                tickFormatter={v => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}
              />
              <Tooltip
                contentStyle={{ background: '#ffffff', border: '1px solid #e5e0ef', borderRadius: 8, fontSize: 12 }}
                labelStyle={{ color: '#1c1530', fontWeight: 600 }}
                formatter={(v) => `R$ ${fmtMoeda(v)}`}
                cursor={{ fill: 'rgba(124,58,237,0.05)' }}
              />
              <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
              <Bar dataKey="Faturamento" fill="#10b981" radius={[4, 4, 0, 0]} cursor={podeEditar ? 'pointer' : 'default'} />
              <Bar dataKey="Despesas" fill="#ef4444" radius={[4, 4, 0, 0]} cursor={podeEditar ? 'pointer' : 'default'} />
              <Bar dataKey="Distribuição" fill="#8b5cf6" radius={[4, 4, 0, 0]} cursor={podeEditar ? 'pointer' : 'default'} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Modal de detalhe do mês */}
      {mesDetalhe && podeEditar && (
        <MesDetalheModal
          ym={mesDetalhe}
          onFechar={() => setMesDetalhe(null)}
          fluxoCaixa={fluxoCaixa}
          setFluxoCaixa={setFluxoCaixa}
          categorias={categoriasDisponiveis}
        />
      )}

      {/* Faturamento por cliente */}
      <div style={s.secao}>
        <h3 style={s.secaoTitulo}>Faturamento por Cliente</h3>
        {faturamentoPorCliente.length === 0
          ? <p style={s.vazio}>Nenhum agendamento no período.</p>
          : faturamentoPorCliente.map(([nome, valor]) => (
            <div key={nome} style={s.linhaCliente}>
              <span style={s.nomeCliente}>{nome}</span>
              <span style={{ color: '#10b981', fontWeight: 600 }}>R$ {fmtMoeda(valor)}</span>
            </div>
          ))}
      </div>

      {/* Horas por cliente */}
      <div style={s.secao}>
        <h3 style={s.secaoTitulo}>Horas por Cliente</h3>
        {horasPorCliente.length === 0
          ? <p style={s.vazio}>Nenhum agendamento no período.</p>
          : horasPorCliente.map(([nome, horas]) => (
            <div key={nome} style={s.linhaCliente}>
              <span style={s.nomeCliente}>{nome}</span>
              <span style={{ color: '#3b82f6', fontWeight: 600 }}>{horas.toFixed(1)}h</span>
            </div>
          ))}
      </div>

      {/* Modal Relatório de Cobrança */}
      {modalRelatorio && (
        <div style={s.overlay} onClick={() => setModalRelatorio(false)}>
          <div className="mobile-modal" style={s.modal} onClick={e => e.stopPropagation()}>
            {/* Cabeçalho */}
            <div style={s.modalHeader}>
              <div>
                <h3 style={s.modalTitulo}>📋 Relatório de Prestação de Contas</h3>
                <p style={s.modalSub}>Selecione as empresas e o período para emitir o relatório de cobrança</p>
              </div>
              <button style={s.btnFechar} onClick={() => setModalRelatorio(false)}><X size={18}/></button>
            </div>

            {/* Filtros do relatório */}
            <div style={s.relFiltros}>
              <div style={s.relFiltroGrupo}>
                <label style={s.label}>Data Inicial</label>
                <input type="date" style={s.input} value={dataInicial} onChange={e => setDataInicial(e.target.value)} />
              </div>
              <div style={s.relFiltroGrupo}>
                <label style={s.label}>Data Final</label>
                <input type="date" style={s.input} value={dataFinal} onChange={e => setDataFinal(e.target.value)} />
              </div>

              {/* Seletor de empresas */}
              <div style={{ ...s.relFiltroGrupo, flex: 2, position: 'relative' }}>
                <label style={s.label}>Empresas / Clientes</label>
                <div style={s.dropdownBtn} onClick={() => setDropdownAberto(!dropdownAberto)}>
                  <span style={{ color: clientesSelecionados.length ? '#1c1530' : '#5f5874' }}>
                    {clientesSelecionados.length === 0
                      ? 'Todas as empresas'
                      : `${clientesSelecionados.length} empresa(s) selecionada(s)`}
                  </span>
                  <ChevronDown size={14} color="#5f5874"/>
                </div>
                {dropdownAberto && (
                  <div style={s.dropdown}>
                    <div style={s.dropdownItem} onClick={() => { setClientesSelecionados([]); setDropdownAberto(false); }}>
                      <input type="checkbox" readOnly checked={clientesSelecionados.length === 0} style={{ accentColor:'#7c3aed' }}/>
                      <span style={{ color: '#1c1530', fontStyle:'italic' }}>Todas as empresas</span>
                    </div>
                    {clientesComAgs.map(c => (
                      <div key={c.id} style={s.dropdownItem} onClick={() => toggleCliente(c.id)}>
                        <input type="checkbox" readOnly checked={clientesSelecionados.includes(String(c.id))} style={{ accentColor:'#7c3aed' }}/>
                        <span style={{ color: '#1c1530' }}>{c.nome}</span>
                      </div>
                    ))}
                    {clientesComAgs.length === 0 && (
                      <div style={{ padding:'8px 12px', color:'#5f5874', fontSize:12 }}>Nenhum cliente com agendamentos no período</div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Chips dos selecionados */}
            {clientesSelecionados.length > 0 && (
              <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                {clientesSelecionados.map(cid => {
                  const c = data.clientes.find(x => String(x.id) === cid);
                  return c ? (
                    <span key={cid} style={s.chip}>
                      {c.nome}
                      <button style={s.chipX} onClick={() => toggleCliente(cid)}><X size={10}/></button>
                    </span>
                  ) : null;
                })}
              </div>
            )}

            {/* Lista de relatórios */}
            <div style={s.relLista}>
              {dadosPorCliente.length === 0 ? (
                <div style={s.vazio}>Nenhum agendamento encontrado no período para os clientes selecionados.</div>
              ) : dadosPorCliente.map(d => (
                <div key={d.cliente.id} style={s.relCard}>
                  {/* Cabeçalho do cliente */}
                  <div style={s.relCardHeader} onClick={() => setClienteExpandido(clienteExpandido === d.cliente.id ? null : d.cliente.id)}>
                    <div style={s.relClienteInfo}>
                      <span style={s.relClienteNome}>{d.cliente.nome}</span>
                      {d.cliente.cnpj && <span style={s.relClienteCnpj}>{d.cliente.cnpj}</span>}
                    </div>
                    <div style={s.relClienteMetrics}>
                      <span style={s.metricChip}>⏱ {d.totalHoras.toFixed(1)}h</span>
                      <span style={{ ...s.metricChip, color:'#10b981', borderColor:'rgba(16,185,129,0.3)', background:'rgba(16,185,129,0.1)' }}>
                        R$ {fmtMoeda(d.total)}
                      </span>
                      <div style={s.relAcoes}>
                        <button style={s.btnExport} onClick={e => { e.stopPropagation(); emitirPDF(d); }} title="Exportar PDF">
                          <Printer size={14}/> PDF
                        </button>
                        <button style={{ ...s.btnExport, background:'rgba(37,99,235,0.15)', borderColor:'rgba(37,99,235,0.3)', color:'#1d4ed8' }}
                          onClick={e => { e.stopPropagation(); emitirWord(d); }} title="Exportar Word">
                          📄 Word
                        </button>
                      </div>
                      {clienteExpandido === d.cliente.id ? <ChevronUp size={16} color="#5f5874"/> : <ChevronDown size={16} color="#5f5874"/>}
                    </div>
                  </div>

                  {/* Tabela expandida */}
                  {clienteExpandido === d.cliente.id && (
                    <div className="mobile-table-wrap" style={s.relTabela}>
                      <table style={{ width:'100%', borderCollapse:'collapse' }}>
                        <thead>
                          <tr>
                            {['Data','Horário','Horas','Descrição','Valor'].map(h => (
                              <th key={h} style={s.th}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {d.agendamentos.map(a => (
                            <tr key={a.id}>
                              <td style={s.td}>{fmtData(a.data)}</td>
                              <td style={s.td}>{a.horaInicio || '—'} – {a.horaFinal || '—'}</td>
                              <td style={s.td}>{parseFloat(a.horas || 0).toFixed(1)}h</td>
                              <td style={{ ...s.td, maxWidth:220, wordBreak:'break-word' }}>{a.observacoes || '—'}</td>
                              <td style={{ ...s.td, color:'#10b981', fontWeight:600, textAlign:'right' }}>R$ {fmtMoeda(calcValorAg(a))}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr>
                            <td colSpan={2} style={{ ...s.td, fontWeight:700, color:'#1c1530', background:'#f7f5fb' }}>TOTAL</td>
                            <td style={{ ...s.td, fontWeight:700, color:'#3b82f6', background:'#f7f5fb' }}>{d.totalHoras.toFixed(1)}h</td>
                            <td style={{ ...s.td, background:'#f7f5fb' }}/>
                            <td style={{ ...s.td, fontWeight:700, color:'#10b981', textAlign:'right', background:'#f7f5fb', fontSize:15 }}>
                              R$ {fmtMoeda(d.total)}
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Rodapé do modal */}
            {dadosPorCliente.length > 1 && (
              <div className="mobile-modal-footer" style={s.modalFooter}>
                <span style={{ color:'#5f5874', fontSize:13 }}>
                  {dadosPorCliente.length} empresa(s) · {dadosPorCliente.reduce((s,d)=>s+d.agendamentos.length,0)} atendimentos ·
                  Total: R$ {fmtMoeda(dadosPorCliente.reduce((s,d)=>s+d.total,0))}
                </span>
                <button style={s.btnExportTodos} onClick={emitirTodosPDF}>
                  <Printer size={15}/> Exportar todos em PDF
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Modal de detalhe do mês ────────────────────────────────────────────
// Mostra lançamentos do fluxoCaixa filtrados pelo mês (RECEITA por
// mesCompetencia; DESPESA por data). Permite editar valor/data/descrição/
// categoria, marcar/excluir como rejeitado e adicionar novos lançamentos.
function MesDetalheModal({ ym, onFechar, fluxoCaixa, setFluxoCaixa, categorias }) {
  const fmtMesLabel = (ym) => {
    const [a, m] = ym.split('-');
    const meses = ['jan','fev','mar','abr','mai','jun','jul','ago','set','out','nov','dez'];
    return `${meses[parseInt(m, 10) - 1]}/${a}`;
  };

  const lancamentosDoMes = useMemo(() => {
    return (fluxoCaixa || []).filter(f => {
      const tipo = (f.tipo || '').toUpperCase();
      if (tipo === 'RECEITA') {
        const mc = f.mesCompetencia || (f.data || '').substring(0, 7);
        return mc === ym;
      }
      return (f.data || '').startsWith(ym);
    }).sort((a, b) => (a.data || '').localeCompare(b.data || ''));
  }, [fluxoCaixa, ym]);

  const totaisMes = useMemo(() => {
    let receita = 0, despesa = 0;
    lancamentosDoMes.forEach(f => {
      if (f.status === 'rejeitado') return;
      if ((f.categoria || '').toLowerCase().includes('antecipa')) {
        if ((f.tipo || '').toUpperCase() !== 'RECEITA') return;
      }
      const v = parseFloat(f.valor || 0);
      if ((f.tipo || '').toUpperCase() === 'RECEITA') receita += v;
      else despesa += v;
    });
    return { receita, despesa };
  }, [lancamentosDoMes]);

  const [editandoId, setEditandoId] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [novoForm, setNovoForm] = useState(null); // null oculto; objeto = formulário aberto

  const iniciarEdicao = (lanc) => {
    setEditandoId(lanc.id);
    setEditForm({
      tipo: lanc.tipo || 'RECEITA',
      descricao: lanc.descricao || '',
      valor: String(lanc.valor || ''),
      data: lanc.data || `${ym}-01`,
      mesCompetencia: lanc.mesCompetencia || ym,
      categoria: lanc.categoria || '',
    });
  };

  const cancelarEdicao = () => { setEditandoId(null); setEditForm(null); };

  const salvarEdicao = () => {
    if (!editForm) return;
    const valor = parseFloat(String(editForm.valor).replace(',', '.'));
    if (!editForm.descricao.trim() || !Number.isFinite(valor) || valor <= 0) {
      toast.erro('Preencha descrição e valor válidos.');
      return;
    }
    setFluxoCaixa(prev => (prev || []).map(f => f.id === editandoId
      ? { ...f, ...editForm, valor, mesCompetencia: editForm.tipo === 'RECEITA' ? (editForm.mesCompetencia || ym) : null }
      : f));
    toast.sucesso('Lançamento atualizado.');
    cancelarEdicao();
  };

  const excluirLanc = (id) => {
    if (!window.confirm('Excluir este lançamento?')) return;
    setFluxoCaixa(prev => (prev || []).filter(f => f.id !== id));
    toast.sucesso('Lançamento excluído.');
  };

  const abrirNovo = (tipo) => {
    setNovoForm({
      tipo,
      descricao: '',
      valor: '',
      data: `${ym}-01`,
      mesCompetencia: ym,
      categoria: tipo === 'RECEITA' ? '' : (categorias[0] || 'Outros'),
    });
  };

  const salvarNovo = () => {
    if (!novoForm) return;
    const valor = parseFloat(String(novoForm.valor).replace(',', '.'));
    if (!novoForm.descricao.trim() || !Number.isFinite(valor) || valor <= 0) {
      toast.erro('Preencha descrição e valor válidos.');
      return;
    }
    const id = Date.now() + Math.floor(Math.random() * 1000);
    const lanc = {
      id,
      tipo: novoForm.tipo,
      descricao: novoForm.descricao.trim(),
      valor,
      data: novoForm.data,
      categoria: novoForm.categoria || null,
      status: 'realizado',
      mesCompetencia: novoForm.tipo === 'RECEITA' ? (novoForm.mesCompetencia || ym) : null,
      origem: 'painel',
    };
    setFluxoCaixa(prev => [...(prev || []), lanc]);
    toast.sucesso(`${novoForm.tipo === 'RECEITA' ? 'Receita' : 'Despesa'} lançada.`);
    setNovoForm(null);
  };

  return (
    <div style={mds.overlay} onClick={onFechar}>
      <div className="mobile-modal" style={mds.modal} onClick={e => e.stopPropagation()}>
        <div style={mds.header}>
          <div>
            <h3 style={mds.titulo}>Lançamentos · {fmtMesLabel(ym)}</h3>
            <p style={mds.sub}>
              Receitas: <span style={{ color: '#10b981', fontWeight: 600 }}>R$ {fmtMoeda(totaisMes.receita)}</span>
              {'  ·  '}
              Despesas: <span style={{ color: '#ef4444', fontWeight: 600 }}>R$ {fmtMoeda(totaisMes.despesa)}</span>
            </p>
          </div>
          <button style={mds.btnFechar} onClick={onFechar}><X size={18} /></button>
        </div>

        {/* Botões para lançar */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button style={{ ...mds.btnAdd, background: 'rgba(16,185,129,0.15)', borderColor: 'rgba(16,185,129,0.4)', color: '#047857' }} onClick={() => abrirNovo('RECEITA')}>
            <Plus size={14} /> Lançar receita
          </button>
          <button style={{ ...mds.btnAdd, background: 'rgba(239,68,68,0.15)', borderColor: 'rgba(239,68,68,0.4)', color: '#dc2626' }} onClick={() => abrirNovo('DESPESA')}>
            <Plus size={14} /> Lançar despesa
          </button>
        </div>

        {/* Form de novo lançamento */}
        {novoForm && (
          <FormLancamento
            form={novoForm}
            setForm={setNovoForm}
            categorias={categorias}
            ym={ym}
            onSalvar={salvarNovo}
            onCancelar={() => setNovoForm(null)}
            isNovo
          />
        )}

        {/* Lista de lançamentos */}
        <div style={mds.lista}>
          {lancamentosDoMes.length === 0 ? (
            <div style={mds.vazio}>Nenhum lançamento neste mês ainda. Use os botões acima para adicionar.</div>
          ) : lancamentosDoMes.map(lanc => {
            const ehReceita = (lanc.tipo || '').toUpperCase() === 'RECEITA';
            const cor = ehReceita ? '#10b981' : '#ef4444';
            const rejeitado = lanc.status === 'rejeitado';

            if (editandoId === lanc.id && editForm) {
              return (
                <div key={lanc.id} style={{ ...mds.linhaCard, borderColor: cor }}>
                  <FormLancamento
                    form={editForm}
                    setForm={setEditForm}
                    categorias={categorias}
                    ym={ym}
                    onSalvar={salvarEdicao}
                    onCancelar={cancelarEdicao}
                  />
                </div>
              );
            }

            return (
              <div key={lanc.id} style={{ ...mds.linhaCard, opacity: rejeitado ? 0.5 : 1 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: cor, background: `${cor}1a`, padding: '1px 8px', borderRadius: 10, border: `1px solid ${cor}40` }}>
                      {ehReceita ? 'RECEITA' : 'DESPESA'}
                    </span>
                    {rejeitado && <span style={{ fontSize: 10, color: '#f59e0b' }}>rejeitado</span>}
                    {lanc.categoria && <span style={{ fontSize: 11, color: '#5f5874' }}>· {lanc.categoria}</span>}
                  </div>
                  <div style={{ fontSize: 13, color: '#1c1530', marginTop: 4, wordBreak: 'break-word' }}>{lanc.descricao || '(sem descrição)'}</div>
                  <div style={{ fontSize: 11, color: '#5f5874', marginTop: 2 }}>
                    {lanc.data ? new Date(lanc.data + 'T12:00:00').toLocaleDateString('pt-BR') : '—'}
                    {ehReceita && lanc.mesCompetencia && lanc.mesCompetencia !== (lanc.data || '').substring(0, 7) && ` · comp. ${fmtMesLabel(lanc.mesCompetencia)}`}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
                  <span style={{ fontSize: 14, fontWeight: 700, color: cor }}>R$ {fmtMoeda(lanc.valor)}</span>
                  <button style={mds.btnIcone} title="Editar" onClick={() => iniciarEdicao(lanc)}>
                    <Pencil size={14} color="#5f5874" />
                  </button>
                  <button style={mds.btnIcone} title="Excluir" onClick={() => excluirLanc(lanc.id)}>
                    <Trash2 size={14} color="#ef4444" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// Subform reaproveitado para novo lançamento e edição
function FormLancamento({ form, setForm, categorias, ym, onSalvar, onCancelar, isNovo = false }) {
  const ehReceita = (form.tipo || '').toUpperCase() === 'RECEITA';
  return (
    <div style={mds.formCard}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <select
          value={form.tipo}
          onChange={e => setForm(prev => ({ ...prev, tipo: e.target.value }))}
          style={mds.input}
        >
          <option value="RECEITA">Receita</option>
          <option value="DESPESA">Despesa</option>
        </select>
        <input
          type="text"
          placeholder="Descrição"
          value={form.descricao}
          onChange={e => setForm(prev => ({ ...prev, descricao: e.target.value }))}
          style={{ ...mds.input, flex: 2, minWidth: 180 }}
        />
        <input
          type="number"
          step="0.01"
          placeholder="Valor"
          value={form.valor}
          onChange={e => setForm(prev => ({ ...prev, valor: e.target.value }))}
          style={{ ...mds.input, width: 120 }}
        />
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span style={mds.lbl}>Data do pagamento/recebimento</span>
          <input
            type="date"
            value={form.data || ''}
            onChange={e => setForm(prev => ({ ...prev, data: e.target.value }))}
            style={mds.input}
          />
        </div>
        {ehReceita && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={mds.lbl}>Mês de competência</span>
            <input
              type="month"
              value={form.mesCompetencia || ym}
              onChange={e => setForm(prev => ({ ...prev, mesCompetencia: e.target.value }))}
              style={mds.input}
            />
          </div>
        )}
        {!ehReceita && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={mds.lbl}>Categoria</span>
            <select
              value={form.categoria || ''}
              onChange={e => setForm(prev => ({ ...prev, categoria: e.target.value }))}
              style={mds.input}
            >
              <option value="">— sem categoria —</option>
              {categorias.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        )}
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 10, justifyContent: 'flex-end' }}>
        <button style={mds.btnSecundario} onClick={onCancelar}>Cancelar</button>
        <button style={mds.btnPrimario} onClick={onSalvar}>
          <Save size={14} /> {isNovo ? 'Lançar' : 'Salvar'}
        </button>
      </div>
    </div>
  );
}

const mds = {
  overlay:      { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 2100, overflowY: 'auto', padding: '2rem 1rem' },
  modal:        { background: '#ffffff', border: '1px solid #e5e0ef', borderRadius: 16, padding: '1.5rem', width: '100%', maxWidth: 760, display: 'flex', flexDirection: 'column', gap: '1rem' },
  header:       { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 },
  titulo:       { fontSize: '1.1rem', fontWeight: 700, color: '#1c1530', margin: 0 },
  sub:          { fontSize: 12, color: '#5f5874', margin: '4px 0 0' },
  btnFechar:    { background: 'transparent', border: 'none', color: '#5f5874', cursor: 'pointer', display: 'flex', flexShrink: 0 },
  btnAdd:       { display: 'flex', alignItems: 'center', gap: 6, border: '1px solid', borderRadius: 8, padding: '6px 12px', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  formCard:     { background: '#ffffff', border: '1px solid #e5e0ef', borderRadius: 10, padding: '0.75rem' },
  input:        { background: '#f7f5fb', border: '1px solid #e5e0ef', borderRadius: 8, color: '#1c1530', padding: '6px 10px', fontSize: 13, outline: 'none' },
  lbl:          { fontSize: 11, color: '#5f5874' },
  btnPrimario:  { display: 'flex', alignItems: 'center', gap: 6, background: '#7c3aed', color: 'white', border: 'none', borderRadius: 8, padding: '6px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  btnSecundario:{ background: 'transparent', color: '#5f5874', border: '1px solid #e5e0ef', borderRadius: 8, padding: '6px 14px', fontSize: 13, cursor: 'pointer' },
  lista:        { display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 360, overflowY: 'auto' },
  linhaCard:    { display: 'flex', alignItems: 'center', gap: 12, background: '#f7f5fb', border: '1px solid #e5e0ef', borderRadius: 10, padding: '10px 12px' },
  btnIcone:     { background: 'transparent', border: '1px solid #e5e0ef', borderRadius: 6, padding: '4px 6px', cursor: 'pointer', display: 'flex' },
  vazio:        { color: '#5f5874', fontSize: 13, textAlign: 'center', padding: '1.5rem', background: '#ffffff', borderRadius: 8, border: '1px dashed #e5e0ef' },
};

const s = {
  container:       { display:'flex', flexDirection:'column', gap:'1.5rem' },
  tituloRow:       { display:'flex', justifyContent:'space-between', alignItems:'center' },
  titulo:          { fontSize:'1.8rem', fontWeight:700, color:'#1c1530', margin:0 },
  btnRelatorio:    { display:'flex', alignItems:'center', gap:6, background:'rgba(124,58,237,0.15)', border:'1px solid rgba(124,58,237,0.4)', color:'#5b21b6', borderRadius:20, padding:'9px 18px', fontSize:13, fontWeight:600, cursor:'pointer' },
  filtroCard:      { background:'#ffffff', border:'1px solid #e5e0ef', borderRadius:12, padding:'1.25rem', display:'flex', flexDirection:'column', gap:'0.75rem' },
  filtroLabel:     { fontSize:12, color:'#5f5874', textTransform:'uppercase', letterSpacing:'0.05em', fontWeight:600 },
  filtroRow:       { display:'flex', gap:'1rem', alignItems:'flex-end', flexWrap:'wrap' },
  filtroGrupo:     { display:'flex', flexDirection:'column', gap:4 },
  label:           { fontSize:12, color:'#5f5874' },
  input:           { background:'#f7f5fb', border:'1px solid #e5e0ef', borderRadius:8, color:'#1c1530', padding:'8px 12px', fontSize:13, outline:'none' },
  btnAtualizar:    { display:'flex', alignItems:'center', gap:6, background:'#7c3aed', color:'white', border:'none', borderRadius:8, padding:'8px 16px', fontSize:13, fontWeight:600, cursor:'pointer' },
  cardsGrid:       { display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(200px, 1fr))', gap:'1rem' },
  card:            { background:'#ffffff', border:'1px solid #e5e0ef', borderRadius:12, padding:'1.25rem', display:'flex', flexDirection:'column', gap:'0.75rem' },
  cardTop:         { display:'flex', justifyContent:'space-between', alignItems:'center' },
  cardLabel:       { fontSize:13, color:'#5f5874' },
  cardValor:       { fontSize:'1.8rem', fontWeight:700 },
  secao:           { background:'#ffffff', border:'1px solid #e5e0ef', borderRadius:12, padding:'1.25rem', display:'flex', flexDirection:'column', gap:'0.5rem' },
  secaoTitulo:     { fontSize:'1rem', fontWeight:700, color:'#1c1530', marginBottom:'0.5rem' },
  linhaCliente:    { display:'flex', justifyContent:'space-between', alignItems:'center', padding:'0.75rem', background:'#f7f5fb', borderRadius:8 },
  nomeCliente:     { color:'#1c1530', fontSize:14 },
  vazio:           { color:'#5f5874', fontSize:13, textAlign:'center', padding:'1rem' },

  // Modal
  overlay:         { position:'fixed', inset:0, background:'rgba(0,0,0,0.75)', display:'flex', alignItems:'flex-start', justifyContent:'center', zIndex:2000, overflowY:'auto', padding:'2rem 1rem' },
  modal:           { background:'#ffffff', border:'1px solid #e5e0ef', borderRadius:16, padding:'1.75rem', width:'100%', maxWidth:860, display:'flex', flexDirection:'column', gap:'1.25rem' },
  modalHeader:     { display:'flex', justifyContent:'space-between', alignItems:'flex-start' },
  modalTitulo:     { fontSize:'1.2rem', fontWeight:700, color:'#1c1530', margin:0 },
  modalSub:        { fontSize:13, color:'#5f5874', margin:'4px 0 0' },
  btnFechar:       { background:'transparent', border:'none', color:'#5f5874', cursor:'pointer', display:'flex', flexShrink:0 },
  relFiltros:      { display:'flex', gap:'1rem', flexWrap:'wrap', alignItems:'flex-end' },
  relFiltroGrupo:  { display:'flex', flexDirection:'column', gap:4, flex:1, minWidth:140 },

  // Dropdown
  dropdownBtn:     { background:'#f7f5fb', border:'1px solid #e5e0ef', borderRadius:8, color:'#1c1530', padding:'8px 12px', fontSize:13, cursor:'pointer', display:'flex', justifyContent:'space-between', alignItems:'center', userSelect:'none' },
  dropdown:        { position:'absolute', top:'100%', left:0, right:0, background:'#f7f5fb', border:'1px solid #e5e0ef', borderRadius:8, zIndex:100, maxHeight:240, overflowY:'auto', marginTop:4 },
  dropdownItem:    { display:'flex', alignItems:'center', gap:8, padding:'8px 12px', cursor:'pointer', fontSize:13 },

  // Chips
  chip:            { display:'flex', alignItems:'center', gap:4, background:'rgba(124,58,237,0.15)', border:'1px solid rgba(124,58,237,0.4)', color:'#5b21b6', borderRadius:16, padding:'3px 10px', fontSize:12, fontWeight:500 },
  chipX:           { background:'transparent', border:'none', color:'#5b21b6', cursor:'pointer', display:'flex', padding:0 },

  // Cards de cliente no relatório
  relLista:        { display:'flex', flexDirection:'column', gap:'0.75rem', maxHeight:420, overflowY:'auto' },
  relCard:         { background:'#f7f5fb', border:'1px solid #e5e0ef', borderRadius:12, overflow:'hidden' },
  relCardHeader:   { display:'flex', justifyContent:'space-between', alignItems:'center', padding:'12px 16px', cursor:'pointer', gap:12, flexWrap:'wrap' },
  relClienteInfo:  { display:'flex', flexDirection:'column', gap:2 },
  relClienteNome:  { fontSize:14, fontWeight:700, color:'#1c1530' },
  relClienteCnpj:  { fontSize:11, color:'#5f5874' },
  relClienteMetrics: { display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' },
  metricChip:      { fontSize:12, fontWeight:600, color:'#8b5cf6', background:'rgba(139,92,246,0.1)', border:'1px solid rgba(139,92,246,0.3)', borderRadius:12, padding:'3px 10px' },
  relAcoes:        { display:'flex', gap:6 },
  btnExport:       { display:'flex', alignItems:'center', gap:4, background:'rgba(124,58,237,0.15)', border:'1px solid rgba(124,58,237,0.3)', color:'#5b21b6', borderRadius:8, padding:'4px 10px', fontSize:12, fontWeight:600, cursor:'pointer' },

  // Tabela expandida
  relTabela:       { padding:'0 12px 12px', overflowX:'auto' },
  th:              { background:'#ffffff', color:'#5f5874', fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.04em', padding:'8px 10px', borderBottom:'1px solid #e5e0ef', textAlign:'left', whiteSpace:'nowrap' },
  td:              { color:'#2d2640', fontSize:13, padding:'8px 10px', borderBottom:'1px solid #eeeaf5' },

  // Rodapé modal
  modalFooter:     { display:'flex', justifyContent:'space-between', alignItems:'center', paddingTop:'0.75rem', borderTop:'1px solid #eeeaf5', flexWrap:'wrap', gap:8 },
  btnExportTodos:  { display:'flex', alignItems:'center', gap:6, background:'#7c3aed', color:'white', border:'none', borderRadius:8, padding:'8px 16px', fontSize:13, fontWeight:600, cursor:'pointer' },
};
